'use strict'
import * as React from 'react'
import { produce } from 'immer'
import { createStore } from 'redux'
import { Provider, useDispatch, useSelector, useStore } from 'react-redux'
import { formatPeriodLabel } from './helpers'

/** 組織階層マスタで扱う最大階層数（第1〜第6）。取込7列形式や列インデックスの意味は validation / helpers のコメントを参照 */
const MAX_HIERARCHY_LEVELS = 6
const TAB_ORG = 'org'
const TAB_HIERARCHY = 'hierarchy'

/**
 * @typedef {Object} DepartmentState
 * @property {Object[]} periodList - 期間一覧
 * @property {string} currentPeriodKey - 選択中期間キー
 * @property {Object[]} currentDepartments - 組織一覧
 * @property {string[][]} currentHierarchy - 組織階層データ（7列形式、登録・エクスポート用）
 * @property {string[][]} hierarchyMasterList - 組織階層 master 2D（ヘッダー付き、API 取得時のみ。表示用）
 * @property {number} hierarchyCount - 階層列数
 * @property {boolean} hierarchyDirty - 階層変更フラグ
 * @property {string} activeTab - アクティブタブ
 * @property {string} orgSortKey - 組織ソートキー
 * @property {'asc' | 'desc'} orgSortDir - ソート方向
 * @property {boolean} loading - ローディング中（期間切替ボタン無効化等）
 * @property {boolean} loadingOrg - 組織一覧取得中
 * @property {boolean} loadingHierarchy - 組織階層取得中
 * @property {number} orgErrorCount - 組織エラー件数
 * @property {number} hierarchyErrorCount - 階層エラー件数
 * @property {Object<string, Object>} orgRowErrors - 組織インポート時の行別エラー { [rowIndex]: { department_code?, department_name?,
 *   ... } }
 * @property {Object<string, Object>} hierarchyRowErrors - 階層インポート時の行別エラー { [rowIndex]: { [columnIndex]: string } }
 * @property {boolean} orgDisplayPreserveOrder - True: 組織一覧をインポート順で表示（ソートしない）。false: API取得後などは組織コード昇順で表示
 * @property {string[]} orgImportReplacedSearchKeys - インポートで一覧を置換した際、DBにあったがファイルに含まれなかった組織の search_key 一覧（登録時に
 *   is_delete: true で送る）
 * @property {string | null} exportDropdown - エクスポートドロップダウン
 * @property {Object[]} segmentList - セグメント一覧
 * @property {Object} periodDataByKey - 期間データキャッシュ
 * @property {boolean} initDone - 初期化完了
 * @property {string} pendingPeriodKey - 保留中期間キー
 * @property {string} topError - 画面上部エラー
 * @property {string[] | null} importedHierarchyHeader - インポート時のファイル1行目（表示ヘッダー）。API取得時はnull
 * @property {boolean} hierarchyShowDrilldownSymbols - 組織階層テーブルで < > を表示するか。master-only 運用では常に false
 * @property {boolean} orgFileFormatError - 組織一覧でCSV/Excel以外のファイルをアップロードしたときのエラー表示用
 * @property {boolean} hierarchyFileFormatError - 組織階層でCSV/Excel以外のファイルをアップロードしたときのエラー表示用
 */

/** @type {DepartmentState} */
const initialState = {
  periodList: [],
  currentPeriodKey: '',
  currentDepartments: [],
  currentHierarchy: [],
  hierarchyMasterList: [],
  hierarchyCount: MAX_HIERARCHY_LEVELS,
  hierarchyDirty: false,
  hierarchyShowDrilldownSymbols: false,
  activeTab: TAB_ORG,
  orgSortKey: 'department_code',
  orgSortDir: 'asc',
  loading: false,
  loadingOrg: false,
  loadingHierarchy: false,
  orgErrorCount: 0,
  hierarchyErrorCount: 0,
  orgRowErrors: {},
  hierarchyRowErrors: {},
  orgDisplayPreserveOrder: false,
  orgImportReplacedSearchKeys: [],
  exportDropdown: null,
  segmentList: [],
  periodDataByKey: {},
  initDone: false,
  pendingPeriodKey: '',
  topError: '',
  importedHierarchyHeader: null,
  orgFileFormatError: false,
  hierarchyFileFormatError: false
}

/**
 * Department reducer
 *
 * @param {DepartmentState} state - 現在の状態
 * @param {{ type: string; payload?: any }} action - Reduxアクション
 * @returns {DepartmentState} 新しい状態
 */
export const departmentReducer = (state = initialState, action) => {
  const { type, payload } = action
  if (type === 'RESET_DEPARTMENT_STATE') return initialState
  return produce(state, (draft) => {
    switch (type) {
      case 'SET_PERIOD_LIST':
        draft.periodList = payload?.periodList ?? []
        if (payload?.currentPeriodKey !== undefined) draft.currentPeriodKey = payload.currentPeriodKey
        if (payload?.hierarchyCount !== undefined) draft.hierarchyCount = payload.hierarchyCount
        draft.orgFileFormatError = false
        draft.hierarchyFileFormatError = false
        break
      case 'SET_CURRENT_PERIOD_KEY':
        draft.currentPeriodKey = payload ?? ''
        draft.orgFileFormatError = false
        draft.hierarchyFileFormatError = false
        break
      case 'SET_DEPARTMENTS':
        draft.currentDepartments = Array.isArray(payload) ? payload : []
        break
      case 'SET_ORG_DISPLAY_PRESERVE_ORDER':
        draft.orgDisplayPreserveOrder = payload === true
        break
      case 'SET_ORG_IMPORT_REPLACED_SEARCH_KEYS':
        draft.orgImportReplacedSearchKeys = Array.isArray(payload) ? [...payload] : []
        break
      case 'SET_HIERARCHY': {
        draft.currentHierarchy = Array.isArray(payload) ? payload : []
        // 空で上書きするときのみ master をクリア（API ロード直後の SET_HIERARCHY(sevenCol) では消さない＝export 時のサフィックス除去のため）
        if (!Array.isArray(payload) || payload.length === 0) {
          draft.hierarchyMasterList = []
        }
        break
      }
      case 'SET_HIERARCHY_IMPORT_HEADER':
        draft.importedHierarchyHeader = Array.isArray(payload) ? payload : null
        break
      case 'SET_HIERARCHY_MASTER_PROCESSED':
        draft.hierarchyMasterList = Array.isArray(payload?.masterList) ? payload.masterList : []
        draft.importedHierarchyHeader = null
        if (payload?.showDrilldownSymbols !== undefined) {
          draft.hierarchyShowDrilldownSymbols = payload.showDrilldownSymbols === true
        }
        break
      case 'SET_HIERARCHY_COUNT':
        draft.hierarchyCount = payload ?? MAX_HIERARCHY_LEVELS
        break
      case 'SET_HIERARCHY_DIRTY':
        draft.hierarchyDirty = payload === true
        break
      case 'SET_ACTIVE_TAB':
        draft.activeTab = payload === TAB_HIERARCHY ? TAB_HIERARCHY : TAB_ORG
        draft.exportDropdown = null
        break
      case 'SET_ORG_SORT':
        if (payload?.key) {
          draft.orgSortKey = payload.key
          draft.orgSortDir = payload.dir ?? 'asc'
        }
        break
      case 'SET_LOADING':
        draft.loading = payload === true
        break
      case 'SET_LOADING_ORG':
        draft.loadingOrg = payload === true
        break
      case 'SET_LOADING_HIERARCHY':
        draft.loadingHierarchy = payload === true
        break
      case 'SET_ORG_ERROR_COUNT': {
        if (payload != null && typeof payload === 'object' && 'errorCount' in payload) {
          draft.orgErrorCount = typeof payload.errorCount === 'number' ? payload.errorCount : 0
          draft.orgRowErrors =
            payload.rowErrors != null && typeof payload.rowErrors === 'object' ? payload.rowErrors : {}
        } else {
          draft.orgErrorCount = typeof payload === 'number' ? payload : 0
          draft.orgRowErrors = {}
        }
        break
      }
      case 'PATCH_ORG_IMPORT_ROW_ERRORS': {
        const raw = payload?.importRowIndex
        const idx =
          typeof raw === 'number' && Number.isFinite(raw)
            ? raw
            : typeof raw === 'string' && raw !== ''
              ? Number.parseInt(raw, 10)
              : NaN
        if (!Number.isFinite(idx) || idx < 0) break
        const key = String(idx)
        const nextErrs = payload?.errors != null && typeof payload.errors === 'object' ? payload.errors : {}
        if (Object.keys(nextErrs).length === 0) {
          delete draft.orgRowErrors[key]
        } else {
          draft.orgRowErrors[key] = nextErrs
        }
        draft.orgErrorCount = Object.values(draft.orgRowErrors).filter(
          (e) => e && typeof e === 'object' && Object.keys(e).length > 0
        ).length
        break
      }
      case 'SET_HIERARCHY_ERROR_COUNT': {
        if (payload != null && typeof payload === 'object' && 'errorCount' in payload) {
          draft.hierarchyErrorCount = typeof payload.errorCount === 'number' ? payload.errorCount : 0
          draft.hierarchyRowErrors =
            payload.rowErrors != null && typeof payload.rowErrors === 'object' ? payload.rowErrors : {}
        } else {
          draft.hierarchyErrorCount = typeof payload === 'number' ? payload : 0
          draft.hierarchyRowErrors = {}
        }
        break
      }
      case 'SET_EXPORT_DROPDOWN':
        draft.exportDropdown = payload ?? null
        break
      case 'SET_SEGMENT_LIST':
        draft.segmentList = Array.isArray(payload) ? payload : []
        break
      case 'SET_INIT_DONE':
        draft.initDone = payload === true
        break
      case 'SET_PENDING_PERIOD_KEY':
        draft.pendingPeriodKey = payload ?? ''
        break
      case 'SET_TOP_ERROR':
        draft.topError = payload ?? ''
        break
      case 'SET_ORG_FILE_FORMAT_ERROR':
        draft.orgFileFormatError = payload === true
        break
      case 'SET_HIERARCHY_FILE_FORMAT_ERROR':
        draft.hierarchyFileFormatError = payload === true
        break
      case 'SET_PERIOD_DATA_BY_KEY':
        if (payload?.key !== undefined && payload?.data) {
          draft.periodDataByKey = { ...draft.periodDataByKey, [payload.key]: payload.data }
        }
        break
      case 'SYNC_PERIOD_END_DATE_FROM_INFO': {
        const pk = payload?.periodKey
        const ed = payload?.endDate != null ? String(payload.endDate).trim() : ''
        if (!pk || !ed) break
        const idx = draft.periodList.findIndex((p) => p.key === pk)
        if (idx === -1) break
        draft.periodList[idx].endDate = ed
        draft.periodList[idx].label = formatPeriodLabel(
          draft.periodList[idx].startDate ?? draft.periodList[idx].key,
          ed
        )
        break
      }
      case 'ADD_DEPARTMENT':
        if (payload) draft.currentDepartments.push(payload)
        break
      case 'UPDATE_DEPARTMENT':
        if (payload?.department_code) {
          const idx = draft.currentDepartments.findIndex((d) => (d.department_code || '') === payload.department_code)
          if (idx !== -1) {
            const prevStatus = draft.currentDepartments[idx].status || ''
            Object.assign(draft.currentDepartments[idx], payload.updates)
            // 'new'（未登録インポート行）はステータスを維持する。削除時に即座に除去されるようにするため
            draft.currentDepartments[idx].status = prevStatus === 'new' ? 'new' : 'updated'
          }
        }
        break
      case 'DELETE_DEPARTMENT':
        if (payload != null) {
          const code = payload.department_code ?? ''
          const idx = draft.currentDepartments.findIndex((d) => (d.department_code || '') === code)
          if (idx !== -1) {
            if ((draft.currentDepartments[idx].status || '') === 'new') {
              draft.currentDepartments.splice(idx, 1)
            } else {
              draft.currentDepartments[idx].status = 'deleted'
            }
          }
        }
        break
      case 'RESTORE_DEPARTMENT':
        if (payload != null) {
          const code = payload.department_code ?? ''
          const idx = draft.currentDepartments.findIndex((d) => (d.department_code || '') === code)
          if (idx !== -1) draft.currentDepartments[idx].status = ''
        }
        break
      case 'ADD_PERIOD':
        if (payload) {
          draft.periodList = [payload, ...draft.periodList]
          draft.currentPeriodKey = payload.key ?? ''
          draft.hierarchyCount = payload.DepartmentHierarchyCount ?? MAX_HIERARCHY_LEVELS
          draft.activeTab = TAB_ORG
        }
        break
      case 'REMOVE_PERIOD':
        if (payload?.key !== undefined) {
          draft.periodList = draft.periodList.filter((p) => p.key !== payload.key)
          if (draft.currentPeriodKey === payload.key) {
            draft.currentPeriodKey = draft.periodList[0]?.key ?? ''
            draft.currentDepartments = []
            draft.currentHierarchy = []
            draft.hierarchyMasterList = []
            draft.orgFileFormatError = false
            draft.hierarchyFileFormatError = false
          }
        }
        break
      case 'CLEAR_PERIOD_DATA':
        draft.currentDepartments = []
        draft.currentHierarchy = []
        break
      case 'MERGE_DEPARTMENTS':
        if (Array.isArray(payload)) draft.currentDepartments = [...draft.currentDepartments, ...payload]
        break
      case 'MERGE_HIERARCHY':
        if (Array.isArray(payload)) {
          draft.currentHierarchy = [...draft.currentHierarchy, ...payload]
          draft.hierarchyDirty = true
        }
        break
      case 'LOAD_PERIOD_SUCCESS':
        if (payload) {
          draft.currentDepartments = payload.departments ?? []
          draft.currentHierarchy = payload.hierarchy ?? []
          draft.segmentList = payload.segmentList ?? []
          draft.hierarchyCount = payload.hierarchyCount ?? MAX_HIERARCHY_LEVELS
          draft.hierarchyDirty = false
          // API取得時はインポート表示用ヘッダー・エラーをクリア（選択中期間のデータのみ表示）
          draft.importedHierarchyHeader = null
          draft.hierarchyRowErrors = {}
          draft.hierarchyErrorCount = 0
          draft.orgRowErrors = {}
          draft.orgErrorCount = 0
          draft.orgDisplayPreserveOrder = false
          draft.orgImportReplacedSearchKeys = []
          if (payload.periodKey && payload.data) {
            draft.periodDataByKey = { ...draft.periodDataByKey, [payload.periodKey]: payload.data }
          }
        }
        break
      case 'REMOVE_UNREGISTERED_PERIOD_AND_REVERT_PREVIOUS':
        if (payload?.removedPeriodKey !== undefined) {
          draft.periodList = draft.periodList.filter((p) => p.key !== payload.removedPeriodKey)
          if (payload?.previousPeriodKey !== undefined && draft.periodList.length > 0) {
            const idx = draft.periodList.findIndex((p) => p.key === payload.previousPeriodKey)
            if (idx !== -1) {
              draft.periodList[idx].endDate = ''
              draft.periodList[idx].label = formatPeriodLabel(
                draft.periodList[idx].startDate ?? draft.periodList[idx].key,
                ''
              )
            }
          }
        }
        break
      case 'PERIOD_CHANGE_CONFIRMED':
        if (payload?.nextKey !== undefined) {
          // 現在の期間が「新規作成で未登録」の場合のみ削除
          // （登録済み期間に変更を加えた場合は削除しない）
          const currentPeriod = draft.periodList.find((p) => p.key === payload.currentKey)
          const isUnregisteredNewPeriod = currentPeriod?.registration_status === 'unregistered'

          if (isUnregisteredNewPeriod) {
            // 未登録の新規期間を削除
            draft.periodList = draft.periodList.filter((p) => p.key !== payload.currentKey)

            // 削除後、先頭期間（最新期間）のendDateをクリア
            if (draft.periodList.length > 0) {
              draft.periodList[0].endDate = ''
              draft.periodList[0].label = formatPeriodLabel(
                draft.periodList[0].startDate ?? draft.periodList[0].key,
                ''
              )
            }
          }

          draft.currentPeriodKey = payload.nextKey
          draft.pendingPeriodKey = ''
          draft.activeTab = TAB_ORG
          draft.currentDepartments = []
          draft.currentHierarchy = []
          draft.hierarchyMasterList = []
          // 期間切替時はインポート表示用のヘッダー・エラーをクリア（他期間でインポートしたファイル名・エラーが表示されないようにする）
          draft.importedHierarchyHeader = null
          draft.hierarchyRowErrors = {}
          draft.hierarchyErrorCount = 0
          draft.orgRowErrors = {}
          draft.orgErrorCount = 0
          draft.orgDisplayPreserveOrder = false
          draft.orgImportReplacedSearchKeys = []
        }
        break
      case 'UPDATE_PERIOD_REGISTRATION_STATUS':
        if (payload?.periodKey !== undefined && payload?.registration_status !== undefined) {
          const idx = draft.periodList.findIndex((p) => p.key === payload.periodKey)
          if (idx !== -1) draft.periodList[idx].registration_status = payload.registration_status
        }
        break
      case 'UPDATE_PERIOD_END_DATE':
        if (payload?.periodKey !== undefined && payload?.endDate !== undefined) {
          const idx = draft.periodList.findIndex((p) => p.key === payload.periodKey)
          if (idx !== -1) {
            draft.periodList[idx].endDate = payload.endDate
            draft.periodList[idx].label = formatPeriodLabel(
              draft.periodList[idx].startDate ?? draft.periodList[idx].key,
              payload.endDate
            )
          }
        }
        break
      case 'REGISTER_SUCCESS':
        draft.currentDepartments = (draft.currentDepartments || []).map((d) => ({ ...d, status: '' }))
        draft.hierarchyDirty = false
        if (payload?.periodKey) {
          const idx = draft.periodList.findIndex((p) => p.key === payload.periodKey)
          if (idx !== -1) draft.periodList[idx].registration_status = 'registered'
        }
        break
      case 'RESET_UI':
        draft.exportDropdown = null
        draft.pendingPeriodKey = ''
        draft.topError = ''
        break
      default:
        break
    }
  })
}

/**
 * Department dispatch hook（B4-001: メモ化）
 *
 * @returns {(type: string, payload?: any) => void} Dispatch function
 */
export const useDepartmentDispatch = () => {
  const dispatch = useDispatch()
  return React.useCallback((type, payload) => dispatch({ type, payload }), [dispatch])
}

export const useDepartmentSelector = useSelector

/**
 * Department Provider component マウント時に毎回新しいストアを生成することで、前回の状態（hierarchyCount等）が残らないようにする
 *
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export const DepartmentProvider = (props) => {
  const [store] = React.useState(() => createStore(departmentReducer))
  return <Provider store={store}>{props.children}</Provider>
}

export { TAB_ORG, TAB_HIERARCHY, MAX_HIERARCHY_LEVELS }
